import { Injectable, OnDestroy, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { first, Observable, Subscription } from "rxjs";

import { v4 } from "uuid";

import { User } from "@hermes/api-model-account";
import { ACCOUNT_PATH, LOGIN_PATH } from "@hermes/fragments/customer";
import {
    ACCOUNT_PAGE_ANGULAR,
    FeatureFlagFacade,
} from "@hermes/states/flipper";
import { TrayFacade } from "@hermes/states/tray";
import { UserFacade } from "@hermes/states/user";
import { RouterService } from "@hermes/utils-generic/services/router";

import { TrayAction } from "./home-page-tray.model";

@Injectable()
export class HomePageTrayService implements OnDestroy {
    private readonly userFacade = inject(UserFacade);
    private readonly router = inject(Router);
    private readonly trayFacade = inject(TrayFacade);
    private readonly routerService = inject(RouterService);
    private readonly route = inject(ActivatedRoute);
    private readonly featureFlagFacade = inject(FeatureFlagFacade);

    private subscription = new Subscription();

    private isAccountPageAngularEnabled$: Observable<boolean> =
        this.featureFlagFacade.isActivated(ACCOUNT_PAGE_ANGULAR);

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public checkUrlToOpenAssociatedTray() {
        const orderId = this.route.snapshot.queryParamMap.get("order_id");

        const trayActions: TrayAction[] = [
            {
                condition: () => this.router.url.includes("#tray-order-list"),
                action: () => this.openTrayOrderList(),
                checkUserConnection: true,
            },
            {
                condition: () =>
                    this.router.url.includes("#tray-order") && !!orderId,
                action: (userDetail?: User) => {
                    if (userDetail) {
                        this.openTrayOrder(orderId, userDetail.email);
                    }
                },
                checkUserConnection: true,
            },
            {
                condition: () => this.router.url.includes("#tray-account"),
                action: () => this.openAccount(),
                checkUserConnection: true,
            },
            {
                condition: () => this.router.url.includes("#tray-subscribe"),
                action: () => this.openTrayNewsletter(),
                checkUserConnection: false,
            },
        ];

        const matchingAction = trayActions.find((trayAction) =>
            trayAction.condition(),
        );

        if (matchingAction) {
            if (matchingAction.checkUserConnection) {
                this.subscription.add(
                    // Opening tray only if user is connected
                    this.userFacade.userDetails$
                        .pipe(first())
                        .subscribe((userDetail) => {
                            if (userDetail?.firstname) {
                                matchingAction.action(userDetail);
                            } else {
                                this.openLoginPage();
                            }
                        }),
                );
            } else {
                // Opening tray if no check of connection. Example : newsletter
                matchingAction.action();
            }
        }
    }

    private openAccount() {
        this.subscription.add(
            this.isAccountPageAngularEnabled$.subscribe(
                (isAccountPageAngularEnabled) => {
                    if (isAccountPageAngularEnabled) {
                        this.openAccountPage();
                    } else {
                        this.openTrayAccount();
                    }
                },
            ),
        );
    }

    private openTrayOrder(orderId: string | null, userEmail: string) {
        this.trayFacade.openTray({
            uuid: v4(),
            name: "tray-order",
            translatedTitle: $localize`:@@block--hermes-checkout--tray--order.order-details:Order details`,
            titleKey: "block--hermes-checkout--tray--order.order-details",
            isDrupalTray: true,
            isOpen: true,
            hasOverlay: true,
            hasCategoryInstitutional: false,
            openCallback: () =>
                localStorage.setItem(
                    "tray-order-data",
                    JSON.stringify({ ref: orderId, email: userEmail }),
                ),
            closeCallback: () => localStorage.removeItem("tray-order-data"),
        });
    }

    private openTrayOrderList() {
        this.trayFacade.openTray({
            uuid: v4(),
            name: "tray-order-list",
            translatedTitle: $localize`:@@hermes-account--profile-info-page.order-history:Order history`,
            titleKey: "hermes-global-translations.orders-title",
            isDrupalTray: true,
            isOpen: true,
            hasOverlay: true,
            hasCategoryInstitutional: false,
        });
    }

    private openTrayAccount() {
        this.trayFacade.openTray({
            uuid: v4(),
            name: "tray-account",
            isDrupalTray: true,
            isOpen: true,
            hasOverlay: true,
            hasCategoryInstitutional: false,
        });
    }

    private openTrayNewsletter() {
        this.trayFacade.openTray({
            uuid: v4(),
            name: "tray-subscribe",
            isDrupalTray: true,
            isOpen: true,
            hasOverlay: true,
            hasCategoryInstitutional: false,
        });
    }

    private openLoginPage() {
        this.routerService.spaRedirect(LOGIN_PATH);
    }

    private openAccountPage() {
        this.routerService.spaRedirect(ACCOUNT_PATH);
    }
}
